import { TicketItem } from "@features/cartDetails/types";
import { BothTicketOptions, GATicket } from "@features/eventPage/types";
import { PriceStatus } from "@utils/enums";

/**
 * Priority order for selecting the "best" area when multiple GA areas exist
 * for the same PriceSectionId + PriceAgeId combination.
 *
 * Lower index = higher priority. "Ended" is omitted intentionally — those rows
 * are filtered out entirely before this ranking runs.
 */
const STATE_PRIORITY: PriceStatus[] = [
  PriceStatus.AVAILABLE,
  PriceStatus.UPCOMING,
  PriceStatus.AWAITINGTRIGGER,
  PriceStatus.SOLDOUT,
  PriceStatus.BOOKEDOUT,
  PriceStatus.LIMITREACHED,
  PriceStatus.NOTAVAILABLE,
];

function statePriority(status: PriceStatus): number {
  const idx = STATE_PRIORITY.indexOf(status);
  return idx === -1 ? STATE_PRIORITY.length : idx;
}

/**
 * Groups ticket option rows by PriceSectionId + PriceAgeId, keeping only the
 * row with the highest-priority availability state for each group.
 *
 * The SP returns one row per GA area × price age combination; this collapses
 * them down to one visible row per ticket type.
 */
function pickBestPerPriceAge(
  options: BothTicketOptions[]
): BothTicketOptions[] {
  const groups = new Map<string, BothTicketOptions>();

  for (const option of options) {
    const key = option.IsCombo
      ? `combo-${option.ComboId}`
      : `${option.PriceSectionId}-${option.PriceAgeId}`;

    const existing = groups.get(key);

    if (
      !existing ||
      statePriority(option.AvailabilityStatus as PriceStatus) <
        statePriority(existing.AvailabilityStatus as PriceStatus)
    ) {
      groups.set(key, option);
    }
  }

  return Array.from(groups.values());
}

export const filterAndMapTicketOptions = (
  data: GATicket | undefined,
  gaAreaIds: number[],
  priceSectionIds: number[],
  isExtraAddOns: boolean,
  basketTickets: TicketItem[]
) => {
  if (!data) return [];

  const filtered = data?.TicketOptions?.filter((to) =>
    isExtraAddOns
      ? to.IsExtra
      : to.GAAreaId
      ? gaAreaIds.includes(to.GAAreaId) &&
        priceSectionIds.includes(to.PriceSectionId) &&
        !to.IsExtra
      : priceSectionIds.includes(to.PriceSectionId) && !to.IsExtra
  )
    // Drop Ended rows before grouping — no customer-facing value in showing them.
    .filter((to) => to.AvailabilityStatus !== PriceStatus.ENDED);

  // Collapse multiple GA areas down to one row per ticket type.
  const best = pickBestPerPriceAge(filtered ?? []);

  return best.map((to) => ({
    // id no longer includes GAAreaId — there is now exactly one row per
    // PriceSection + PriceAge pair, so the simpler key is sufficient.
    id: to.IsCombo
      ? `combo-${to.ComboId}`
      : `${to.PriceSectionId}-${to.PriceAgeId}`,
    gaAreaId: to.GAAreaId,
    priceSectionId: to.PriceSectionId,
    priceAgeId: to.PriceAgeId,
    priceId: to.PriceId,
    type: to.Name,
    info: to.IsCombo ? to.Description : to.PriceAgeDescription,
    price: to.Price,
    discountPrice: to.DiscountPrice,
    min: to.MinTickets ?? 0,
    max: to.MaxTickets,
    minPrice: to.Price,
    maxPrice: to.Price,
    priceMinimum: to.PriceMinimum,
    priceMaximum: to.PriceMaximum,
    bookedTickets: to.IsCombo
      ? to.NumberOfTimesUsed
      : basketTickets.filter(
          (bt) =>
            bt.PriceId === to.PriceId &&
            bt.PriceAgeId === to.PriceAgeId &&
            bt.PriceSectionId === to.PriceSectionId &&
            !bt.Combo
        ).length,
    bookedFromCombo: basketTickets.filter(
      (bt) =>
        bt.PriceId === to.PriceId &&
        bt.PriceAgeId === to.PriceAgeId &&
        bt.PriceSectionId === to.PriceSectionId &&
        bt.Combo
    ).length,
    bookingLimit: to.BookingLimit,
    isCombo: to.IsCombo,
    status: to.AvailabilityStatus,
    comboId: to.ComboId,
    isExtra: to.IsExtra,
    isPromo: to.IsPromo,
    areaName: to.AreaName,
    priceSchemeName: to.PriceSchemeName ? to.PriceSchemeName.trim() : "",
    priceSectionName: to.PriceSectionName ? to.PriceSectionName.trim() : "",
    currency: data.Currency,
    priceAgeStartDate: to.PriceAgeStartDate,
    priceAgeEndDate: to.PriceAgeEndDate,
    // New: populated when status === UPCOMING, null otherwise.
    availableFrom: to.AvailableFrom ?? null,
    order: to.Ordering,
  }));
};
